import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import { getBasicPieChartOptions } from '../chartWidgetUtility';

import { useTheme } from "@mui/material/styles";
import WidgetWraper from 'components/Common/WidgetWraper';

interface WidgetProps {
    isLoading: boolean;
    seriesData?: any;
    hasTooltip?: boolean;
    onSliceClick?: (event: ECElementEvent) => void;
    unit?: "PB" | "TB" | "GB";
    showDollar?: boolean;
    customSize?: string;
    colorMapping?: any;
    showPercentage?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    baseHeightRem?: number;
    baseWidthRem?: number;
    expandedScale?: number;
}

const BasicPiechart: React.FC<WidgetProps> = ({
    isLoading = false,
    seriesData = [],
    onSliceClick,
    unit = "GB",
    customSize = '200px',
    showDollar = false,
    colorMapping,
    showPercentage = false,
    // expand props
    isExpanded = false,
    baseHeightRem = 12,
    baseWidthRem = 20,
    expandedScale = 1.5
}) => {
    const theme= useTheme();
    const colors = theme.palette;
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const echartsRef = useRef<any>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const defaultOptions = {
        graphic: [
            {
                type: 'circle',
                left: 'center',
                top: 'center',
                shape: {
                    r: 100
                },
                style: {
                    fill: "#e4e4e4"
                }
            }
        ],
        title: {
            show: true,
            textStyle: {
                color: colors.widget.text.main,
                fontWeight: 'normal',
                fontSize: 16
            },
            text: 'No data',
            left: 'center',
            top: 'center'
        },
        backgroundColor: colors.widget.background.card
    };

    const result = getBasicPieChartOptions({
        theme,
        seriesData,
        unit,
        containerWidth: chartContainerRef?.current?.offsetWidth,
        colorMapping: colorMapping ?? {},
        showDollar,
        showPercentage,
    });

    const adjustedOption : any = useMemo(() => {
        const width = containerWidth || chartContainerRef.current?.offsetWidth || 500;

        if (!seriesData.length) return defaultOptions;

        return {
            ...result,
            series: result.series.map((s: any) => ({
                ...s,
                radius: isExpanded && width < 820 ? "40%" : s.radius || "65%"
            })),
        };
    }, [seriesData, containerWidth, isExpanded, result]);


    // compute doubled sizes when expanded
    const cardHeightRem = useMemo(
        () => (isExpanded ? baseHeightRem * expandedScale : baseHeightRem),
        [isExpanded, baseHeightRem, expandedScale]
    );

    // compute an effective chart min-height so the chart grows with the card
    const effectiveChartMinHeight = useMemo(() => {
        const headerReservePx = 120;
        const cardHeightPx = cardHeightRem * 16;
        const fallbackCustomPx = parseInt(String(customSize), 10) || 0;
        const suggestedPx = Math.max(fallbackCustomPx, Math.max(160, cardHeightPx - headerReservePx));
        return `${suggestedPx}px`;
    }, [customSize, cardHeightRem]);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const observer = new ResizeObserver(() => {
            const newWidth = chartContainerRef.current?.offsetWidth || 0;
            setContainerWidth(newWidth);

            setTimeout(() => {
            echartsRef.current?.getEchartsInstance()?.resize();
            }, 50);
        });

        observer.observe(chartContainerRef.current);

        return () => observer.disconnect();
    }, []);


    return (
        <WidgetWraper sx={{ padding: 0 }}>
            <Box
                sx={{
                    backgroundColor: `${colors.widget.background.card} !important`,
                    display: 'flex',
                    flexDirection: 'Column',
                    color: `${colors.widget.text.main} !important`,
                    height: isExpanded ? `100%` : `${cardHeightRem}rem`, // + 16px for padding
                    width: '100%',
                    position: 'relative', // anchor the button
                    transition: 'height 300ms ease, width 300ms ease',
                    willChange: 'height, width',
                    overflow: 'hidden',
                    border: "1px solid rgba(0,0,0,0.12)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                }}
            >
                {!isLoading ? (
                    <Stack flexGrow={1} ref={chartContainerRef} minHeight={effectiveChartMinHeight}>
                        <ReactECharts
                            ref={echartsRef} // we can call resize()
                            style={{ width: '100%', height: '100%' }}
                            option={adjustedOption}
                            onEvents={{
                                click: (params: ECElementEvent) => {
                                    if (onSliceClick && seriesData.length) {
                                        onSliceClick(params);
                                    }
                                }
                            }}
                            opts={{ renderer: 'canvas', width: 'auto' }}
                            notMerge
                            lazyUpdate
                        />
                    </Stack>
                ) : (
                    <LoaderComponent pt={20}/>
                )}
            </Box>
        </WidgetWraper>
    );
};

export default BasicPiechart;
